import FloatingTooltip from "@components/common/FloatingTooltip";
import TravelTimeline from "@components/common/Timeline/TravelTimeline";
import { formatMinutes } from "@utils/formatDate";
import { useTranslation } from "react-i18next";
import {
  Journey,
  PlaneCabinClass,
  Product,
  Segment,
  TrainCabinClass,
  Transportation,
} from "@gen/travel/v1/common_pb";
import { localDateTimeToDate } from "@utils/date";

type DetailedTimelineProps = {
  journey: Journey;
  products: Product[];
};

export default function DetailedTimeline({
  journey,
  products,
}: DetailedTimelineProps) {
  const { t } = useTranslation();

  function connectionDuration(index: number) {
    if (journey.segments.length < index + 1) {
      return;
    }
    const segment = journey.segments[index];
    const nextSegment = journey.segments[index + 1];

    if (!segment.schedule?.arrival || !nextSegment.schedule?.departure) {
      return;
    }
    const arrival = localDateTimeToDate(segment.schedule?.arrival);
    const departure = localDateTimeToDate(nextSegment.schedule?.departure);

    return Math.floor((departure.getTime() - arrival.getTime()) / (1000 * 60));
  }

  function getProductOfSegment(segment: Segment) {
    return products.find((p) => p.segmentId === segment.id);
  }

  function getClassLabel(segment: Segment): string {
    const product = getProductOfSegment(segment);
    switch (product?.cabinClass?.cabinClass.case) {
      case "trainCabinClass":
        switch (product?.cabinClass?.cabinClass.value) {
          case TrainCabinClass.SECOND:
            return t("booking.conditions.second");
          case TrainCabinClass.FIRST:
            return t("booking.conditions.first");
        }
        break;
      case "planeCabinClass":
        switch (product?.cabinClass?.cabinClass.value) {
          case PlaneCabinClass.ECONOMY:
            return t("booking.conditions.economy");
          case PlaneCabinClass.ECONOMY_PREMIUM:
            return t("booking.conditions.economyPremium");
          case PlaneCabinClass.BUSINESS:
            return t("booking.conditions.business");
        }
    }

    return "";
  }

  return journey.segments.map((segment: Segment, index: number) => (
    <div key={index}>
      <TravelTimeline journey={segment}>
        <div className="my-2 flex flex-col items-start justify-center text-xs h-10">
          {segment.transport?.type === Transportation.TRAIN && (
            <div className="flex flex-row items-start justify-between w-full">
              <div className="flex flex-col min-w-fit">
                <span className="uppercase">
                  <span>{segment.transport?.name} </span>
                  <span className="normal-case">
                    {t("product.numberShort")}
                  </span>
                  {segment.transport?.number}
                </span>
                <span>{getClassLabel(segment)}</span>
              </div>
              {segment.transport?.logo && (
                <FloatingTooltip content={segment.transport?.name}>
                  <img
                    src={segment.transport?.logo}
                    alt="carrier-logo"
                    className="ml-1 max-w-20"
                  />
                </FloatingTooltip>
              )}
            </div>
          )}
          {segment.transport?.type === Transportation.PLANE && (
            <div className="flex items-center text-xs my-2">
              {segment.transport?.number} {t("product.operatedBy")}{" "}
              <FloatingTooltip content={segment.operatingCarrier?.name}>
                <img
                  src={segment.operatingCarrier?.logo}
                  alt={segment.operatingCarrier?.name}
                  className="ml-1 max-h-6"
                />
              </FloatingTooltip>
            </div>
          )}
        </div>
      </TravelTimeline>
      {segment.transport?.type === Transportation.PLANE && (
        <div className="flex items-center text-xs my-2">
          {t("product.soldBy")}{" "}
          <FloatingTooltip content={segment.marketingCarrier?.name}>
            <img
              src={segment.marketingCarrier?.logo}
              alt="carrier-logo"
              className="ml-1 max-h-6"
            />
          </FloatingTooltip>
        </div>
      )}
      {journey.segments.length > index + 1 && (
        <p className="my-2 ml-6 text-xs">
          {formatMinutes(connectionDuration(index) || 0)}{" "}
          {t("product.connectionTime")}
        </p>
      )}
    </div>
  ));
}
